import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { RoleForm } from "../components/access/RoleForm";
import { useRoles } from "../components/operators/AccessPicker";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, Notice, SkeletonRows } from "../components/ui/Feedback";
import { Pill } from "../components/ui/Status";
import type { Column } from "../components/ui/Table";
import { Cell, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import { Metric } from "../components/ui/Text";
import { del } from "../lib/api";
import { count } from "../lib/format";
import { groupPermissions, permissionCount } from "../lib/permissions";
import { deleteConsequence, sortRoles } from "../lib/roles";
import { useSession } from "../lib/session";
import type { DeletedRole, Role } from "../lib/types";

/**
 * Roles: named sets of permissions, handed to operators.
 *
 * ## Permissions are fixed, roles are not
 *
 * The permission list is part of the code — each one is a check some route makes —
 * so it is not edited here. What is edited is how they are bundled: a "Support"
 * role that can see orders and disable customers but touch no price is a row on
 * this page, not a release.
 *
 * ## Administrator
 *
 * Built in, and the one role the server will not let anybody edit or delete. It
 * grants every permission, including ones a later release adds, which is what
 * makes it a safe answer to "who can always get back in". It is shown here for
 * completeness and is read-only.
 *
 * ## Deleting
 *
 * Takes the role from everyone holding it, immediately. The confirmation names how
 * many people that is, because the alternative is discovering it from the ones who
 * can no longer do their job.
 */

const COLUMNS: readonly Column[] = [
  { key: "role", label: "Role", width: "34%" },
  { key: "permissions", label: "Permissions", width: "22%" },
  { key: "members", label: "Operators", width: "12%", numeric: true },
];

const ACTION_COLUMN: Column = { key: "act", label: "", width: "32%" };

type Panel =
  | { readonly kind: "none" }
  | { readonly kind: "create" }
  | { readonly kind: "edit"; readonly id: string }
  | { readonly kind: "delete"; readonly id: string };

export function Roles() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const roles = useRoles();
  const [panel, setPanel] = useState<Panel>({ kind: "none" });
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const rows = sortRoles(roles.data ?? []);
  const canCreate = can("roles.create");
  const canUpdate = can("roles.update");
  const canDelete = can("roles.delete");
  const showActions = canUpdate || canDelete;

  const remove = useMutation({
    mutationFn: (role: Role) => del<DeletedRole>(`/roles/${role.id}`),
    onSuccess: (result, role) => {
      setPanel({ kind: "none" });
      setNotice(
        result.removedFrom === 0
          ? `${role.name} deleted.`
          : `${role.name} deleted and removed from ${result.removedFrom} operator${result.removedFrom === 1 ? "" : "s"}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["roles"] });
      void queryClient.invalidateQueries({ queryKey: ["operators"] });
    },
  });

  const editing = panel.kind === "edit" ? rows.find((row) => row.id === panel.id) : undefined;
  const custom = rows.filter((row) => !row.system).length;

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Roles"
        metrics={
          <>
            <Metric label="Roles" value={count(rows.length)} />
            <Metric
              label="Editable"
              value={count(custom)}
              tone={custom > 0 ? "ink" : "muted"}
              hint="all but Administrator"
            />
          </>
        }
        actions={
          canCreate && panel.kind !== "create" ? (
            <Button
              variant="primary"
              onClick={() => {
                setNotice(undefined);
                setPanel({ kind: "create" });
              }}
            >
              New role
            </Button>
          ) : null
        }
        lede="A role is a named set of permissions. Operators hold roles, and can be given single permissions on top; what they can do is the two added together."
      />

      {panel.kind === "create" ? (
        <RoleForm
          existing={rows}
          onSaved={(role) => {
            setPanel({ kind: "none" });
            setNotice(`${role.name} created.`);
          }}
          onCancel={() => setPanel({ kind: "none" })}
        />
      ) : null}

      {editing === undefined ? null : (
        <RoleForm
          key={editing.id}
          role={editing}
          existing={rows}
          onSaved={(role) => {
            setPanel({ kind: "none" });
            setNotice(`${role.name} saved.`);
          }}
          onCancel={() => setPanel({ kind: "none" })}
        />
      )}

      {notice === undefined ? null : <Notice tone="ok">{notice}</Notice>}
      {roles.isError ? (
        <ErrorNotice error={roles.error} onRetry={() => void roles.refetch()} />
      ) : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      <TableFrame>
        <TableHead columns={showActions ? [...COLUMNS, ACTION_COLUMN] : COLUMNS} />
        {roles.isPending ? (
          <SkeletonRows rows={4} columns={COLUMNS.length + (showActions ? 1 : 0)} />
        ) : (
          <tbody>
            {rows.map((row, index) => (
              <RoleRow
                key={row.id}
                row={row}
                index={index}
                showActions={showActions}
                canUpdate={canUpdate}
                canDelete={canDelete}
                isConfirmingDelete={panel.kind === "delete" && panel.id === row.id}
                isDeleting={remove.isPending && remove.variables?.id === row.id}
                onEdit={() => {
                  setNotice(undefined);
                  setPanel({ kind: "edit", id: row.id });
                }}
                onAskDelete={() => {
                  setNotice(undefined);
                  setPanel({ kind: "delete", id: row.id });
                }}
                onCancelDelete={() => setPanel({ kind: "none" })}
                onConfirmDelete={() => remove.mutate(row)}
              />
            ))}
          </tbody>
        )}
      </TableFrame>

      {!roles.isPending && !roles.isError && rows.length === 0 ? (
        <EmptyState
          title="No roles."
          detail="Administrator is created by the migration, so an empty list means the server has not been migrated yet."
        />
      ) : null}
    </>
  );
}

function RoleRow({
  row,
  index,
  showActions,
  canUpdate,
  canDelete,
  isConfirmingDelete,
  isDeleting,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}: {
  readonly row: Role;
  readonly index: number;
  readonly showActions: boolean;
  readonly canUpdate: boolean;
  readonly canDelete: boolean;
  readonly isConfirmingDelete: boolean;
  readonly isDeleting: boolean;
  readonly onEdit: () => void;
  readonly onAskDelete: () => void;
  readonly onCancelDelete: () => void;
  readonly onConfirmDelete: () => void;
}) {
  return (
    <TableRow index={index}>
      <Cell>
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-2 text-ink">
            {row.name}
            {row.system ? <Pill tone="idle">built in</Pill> : null}
          </span>
          {row.description === null ? null : (
            <span className="text-micro tracking-normal normal-case text-ink-faint">
              {row.description}
            </span>
          )}
        </div>
      </Cell>
      <Cell>
        {row.grantsAll ? (
          <span className="text-ink">Has every permission</span>
        ) : (
          <details className="text-micro tracking-normal normal-case text-ink-faint">
            <summary className="cursor-pointer text-data text-ink hover:text-ink">
              {permissionCount(row.permissions)}
            </summary>
            <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0">
              {groupPermissions(row.permissions).map((group) => (
                <li key={group.group}>
                  <span className="font-semibold text-ink-muted">{group.group}:</span>{" "}
                  {group.permissions.map((info) => info.label).join(", ")}
                </li>
              ))}
            </ul>
          </details>
        )}
      </Cell>
      <Cell numeric muted={row.memberCount === 0}>
        {row.memberCount === 0 ? "—" : count(row.memberCount)}
      </Cell>
      {showActions ? (
        <Cell>
          {row.system ? (
            <span className="text-micro tracking-normal normal-case text-ink-faint">
              Cannot be edited or deleted.
            </span>
          ) : isConfirmingDelete ? (
            <div className="flex flex-col gap-2">
              <span className="text-micro tracking-normal normal-case text-ink">
                {deleteConsequence(row)}
              </span>
              <div className="flex flex-wrap gap-2">
                <Button variant="danger" pending={isDeleting} onClick={onConfirmDelete}>
                  Delete {row.name}
                </Button>
                <Button variant="ghost" onClick={onCancelDelete}>
                  Keep it
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {canUpdate ? (
                <Button variant="ghost" onClick={onEdit}>
                  Edit
                </Button>
              ) : null}
              {canDelete ? (
                <Button variant="danger" onClick={onAskDelete}>
                  Delete
                </Button>
              ) : null}
            </div>
          )}
        </Cell>
      ) : null}
    </TableRow>
  );
}
